// src/ConsumerApp/Screens/Account/views/WalletView.js

import React, { useState, useMemo } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import WalletIcon from '../../../../assets/icons/Wallet.svg';
import ClockCounterClockwise from '../../../../assets/icons/ClockCounterClockwise.svg';

// Shared Hooks
import { useGetWalletBalance, useGetWalletHistory } from '../../../api/wallet/wallet.hooks';

export default function WalletView() {
  // 1. Session Context (Read from browser storage directly)
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. States
  const [showHistory, setShowHistory] = useState(true);

  // 3. API Queries
  const { data: balanceData, isLoading: isLoadingBalance } = useGetWalletBalance(customerId);
  const { data: historyResponse, isLoading: isLoadingHistory } = useGetWalletHistory(customerId, 1, 10);

  // 4. Formatted History Mapping
  const mappedHistory = useMemo(() => {
    const rawHistory = historyResponse?.wallet_history || historyResponse?.data?.wallet_history || [];

    return rawHistory.map((item) => {
      const isDebit = item.transaction_type?.toLowerCase() === 'debit';
      const rawAmount = Number(item.amount) || 0;

      return {
        id: item.transaction_id || item._id,
        title: item.title || (item.order_number ? `Order #${item.order_number}` : 'Transaction'),
        amount: `${isDebit ? '-' : '+'}₹${rawAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
        isDebit,
        date: item.created_at
          ? new Date(item.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '',
      };
    });
  }, [historyResponse]);

  const availableBalance = Number(balanceData?.available_wallet_balance ?? 0);

  return (
    <div style={styles.container}>
      {/* ── Page Header ── */}
      <div style={styles.pageHeader}>
        <div>
          <h2 style={styles.headerTitle}>Wallet & Build Points</h2>
          <span style={styles.headerSubtitle}>
            Track your available credit, rewards balance, and transaction statement
          </span>
        </div>
      </div>

      {/* ── Top Row: Balance Card & Build Points Card ── */}
      <div style={styles.summaryRow}>
        {/* Wallet Gradient Card */}
        <div style={styles.walletCard}>
          <div style={styles.walletCardTop}>
            <div style={styles.titleContainer}>
              <div style={styles.walletIconWrap}>
                <img
                  src={WalletIcon}
                  alt="wallet"
                  style={{
                    width: 16,
                    height: 16,
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </div>
              <span style={styles.walletTitle}>Wallet Balance</span>
            </div>

            <button
              type="button"
              style={styles.toggleBtn}
              onClick={() => setShowHistory((prev) => !prev)}
            >
              <img
                src={ClockCounterClockwise}
                alt="history"
                style={{
                  width: 12,
                  height: 12,
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.9,
                }}
              />
              <span>{showHistory ? 'Hide Statement' : 'Show Statement'}</span>
            </button>
          </div>

          <div style={styles.balanceRow}>
            {isLoadingBalance ? (
              <span style={styles.balanceLoading}>Loading balance...</span>
            ) : (
              <span style={styles.balanceValue}>
                ₹{availableBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>

          <p style={styles.walletSubText}>
            Wallet is your direct procurement cash. Automatically redeemable during checkout.
          </p>
        </div>

        {/* Build Points Card */}
        <div style={styles.pointsCard}>
          <div style={styles.pointsHeader}>
            <span style={styles.pointsLabel}>Build Points</span>
            <span style={styles.badgeComingSoon}>Coming Soon</span>
          </div>
          <span style={styles.pointsValue}>0</span>
          <span style={styles.pointsHint}>
            Earn reward points as you source structural supplies with Brickbucket.
          </span>
        </div>
      </div>

      {/* ── Transaction History Statement ── */}
      {showHistory && (
        <div style={styles.historyCard}>
          <div style={styles.historyHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src={ClockCounterClockwise}
                alt="statement"
                style={{ width: 16, height: 16, opacity: 0.7 }}
              />
              <h3 style={styles.historySectionTitle}>Recent Transactions</h3>
            </div>
            <span style={styles.historyCountBadge}>{mappedHistory.length} entries</span>
          </div>

          {isLoadingHistory ? (
            <div style={styles.loadingBox}>
              <span>Fetching statement history...</span>
            </div>
          ) : mappedHistory.length === 0 ? (
            <div style={styles.emptyBox}>
              <div style={styles.emptyIconCircle}>
                <img
                  src={WalletIcon}
                  alt="empty"
                  style={{ width: 22, height: 22, opacity: 0.35 }}
                />
              </div>
              <h4 style={styles.emptyTitle}>No transaction history</h4>
              <p style={styles.emptySubtitle}>
                Transactions, cashbacks, and order debits will appear here once executed.
              </p>
            </div>
          ) : (
            <div style={styles.transactionList}>
              {mappedHistory.map((item, index) => {
                const isLast = index === mappedHistory.length - 1;

                return (
                  <div
                    key={item.id || index}
                    style={{
                      ...styles.itemRow,
                      borderBottom: isLast ? 'none' : '1px solid #F1F5F9',
                    }}
                  >
                    <div style={styles.itemLeft}>
                      <span style={styles.itemTitle}>{item.title}</span>
                      <span style={styles.itemDate}>{item.date}</span>
                    </div>

                    <div style={styles.itemRight}>
                      <span
                        style={{
                          ...styles.itemAmount,
                          color: item.isDebit ? '#C7061F' : '#16A34A',
                        }}
                      >
                        {item.amount}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 16,
  },
  headerTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#707B8C',
    marginTop: 4,
    display: 'block',
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
  },
  walletCard: {
    background: 'linear-gradient(135deg, #850515 0%, #E9001E 100%)',
    borderRadius: 16,
    padding: '20px 24px',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 140,
    boxShadow: '0 4px 20px rgba(233, 0, 30, 0.18)',
    position: 'relative',
  },
  walletCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  walletIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  walletTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  toggleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
    border: 'none',
    borderRadius: 999,
    padding: '4px 10px',
    color: '#FFFFFF',
    fontSize: 11.5,
    fontWeight: '600',
    cursor: 'pointer',
    outline: 'none',
    transition: 'background-color 0.15s ease',
  },
  balanceRow: {
    margin: '12px 0 8px 0',
  },
  balanceValue: {
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: '-0.5px',
  },
  balanceLoading: {
    fontSize: 16,
    opacity: 0.8,
  },
  walletSubText: {
    margin: 0,
    fontSize: 11.5,
    opacity: 0.9,
    lineHeight: '16px',
  },
  pointsCard: {
    backgroundColor: '#FFF7E8',
    border: '1px solid #F4D9A4',
    borderRadius: 16,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 140,
  },
  pointsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pointsLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#7A4B00',
  },
  badgeComingSoon: {
    fontSize: 10.5,
    fontWeight: '700',
    backgroundColor: '#FDE68A',
    color: '#78350F',
    padding: '2px 8px',
    borderRadius: 999,
  },
  pointsValue: {
    fontSize: 28,
    fontWeight: '800',
    color: '#2B2A29',
    margin: '8px 0 4px 0',
  },
  pointsHint: {
    fontSize: 11.5,
    color: '#7A6A53',
    lineHeight: '16px',
  },
  historyCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  historyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #F1F5F9',
    paddingBottom: 12,
  },
  historySectionTitle: {
    margin: 0,
    fontSize: 15.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  historyCountBadge: {
    fontSize: 11.5,
    color: '#707B8C',
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '2px 8px',
    borderRadius: 6,
  },
  transactionList: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  itemTitle: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#2B2A29',
  },
  itemDate: {
    fontSize: 11.5,
    color: '#707B8C',
  },
  itemRight: {
    display: 'flex',
    alignItems: 'center',
  },
  itemAmount: {
    fontSize: 14,
    fontWeight: '700',
  },
  loadingBox: {
    padding: '36px 0',
    textAlign: 'center',
    fontSize: 13,
    color: '#707B8C',
  },
  emptyBox: {
    padding: '36px 16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  emptyTitle: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  emptySubtitle: {
    margin: '4px 0 0 0',
    fontSize: 12,
    color: '#707B8C',
  },
};